import React, { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PenNib, PaperPlaneTilt, FilePdf, UploadSimple, Eye } from "@phosphor-icons/react";
import { SignaturePositionEditor } from "./SignaturePositionEditor";
import { RecipientPicker } from "@/components/RecipientPicker";

/**
 * Dialog "Invia documento da firmare" — Iter113.
 *
 * L'operatore/admin scrive un breve messaggio e carica un PDF. Il backend:
 *  1. Salva il PDF nell'object storage.
 *  2. Invia una email con il PDF in allegato al cliente finale
 *     (owner_email dell'immobile), o al professionista come fallback.
 *  3. L'email contiene un link firmato a una pagina pubblica di firma.
 */
export function SendForSignatureDialog({
  open, onClose, practiceId, serviceName, clientEmail, onSent,
}) {
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [sendTo, setSendTo] = useState(null);
  const [positions, setPositions] = useState([]); // Iter208.94
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fileUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (fileUrl) URL.revokeObjectURL(fileUrl); }, [fileUrl]);

  useEffect(() => {
    if (!open) {
      setMessage("");
      setFile(null);
      setSendTo(null);
      setPositions([]);
      setResult(null);
      setPreviewOpen(false);
    }
  }, [open]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setFile(null);
    if (f.type !== "application/pdf") {
      toast.error("Solo file PDF sono accettati");
      e.target.value = "";
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error("File troppo grande (max 20 MB)");
      e.target.value = "";
      return;
    }
    setFile(f);
    setPositions([]);  // reset positions on new file
  };

  const submit = async () => {
    if (!file) {
      toast.error("Seleziona il PDF da inviare");
      return;
    }
    if (!positions.length) {
      toast.error("Posiziona almeno un punto firma sul documento");
      return;
    }
    if (!sendTo) {
      toast.error("Scegli a chi inviare il documento");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("message", message.trim());
      fd.append("signature_positions", JSON.stringify(positions));
      fd.append("send_to", sendTo);
      const r = await api.post(
        `/practices/${practiceId}/send-for-signature`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(r.data);
      if (r.data?.email_sent) {
        toast.success(
          `Email inviata a ${r.data?.recipient?.email} (${
            r.data?.recipient?.type === "client" ? "cliente finale" : "professionista"
          })`,
        );
      } else if (r.data?.email_error) {
        toast.warning(`Richiesta salvata ma email non partita: ${r.data.email_error}`);
      }
      onSent && onSent(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio documento");
    } finally {
      setSending(false);
    }
  };

  const closeDialog = () => {
    if (sending) return;
    onClose && onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); }}>
      <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto" data-testid="send-for-signature-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenNib size={18} weight="bold" /> Invia documento da firmare
          </DialogTitle>
          <DialogDescription>
            Carica un PDF e scegli se inviarlo al <strong>professionista</strong> o al{" "}
            <strong>cliente finale</strong>: riceverà un'email con il documento in allegato e un link a una
            pagina di firma dedicata.
            {serviceName && <> Pratica: <strong>{serviceName}</strong>.</>}
          </DialogDescription>
        </DialogHeader>

        {result?.ok ? (
          <div className="space-y-3 py-3">
            {result.email_sent === false ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="signature-result-warning">
                ⚠️ Richiesta salvata, ma l'email a <strong>{result.recipient?.email}</strong> non è partita
                {result.email_error ? `: ${result.email_error}` : "."} Puoi inoltrare manualmente il link qui sotto.
              </div>
            ) : (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              ✓ Documento inviato con successo a{" "}
              <strong>{result.recipient?.email}</strong>{" "}
              ({result.recipient?.type === "client" ? "cliente finale" : "professionista"}).
            </div>
            )}
            <div className="text-xs text-muted-foreground">
              Link pubblico di firma (valido 7 giorni):
              <div className="mt-1 p-2 rounded bg-slate-50 border font-mono text-[11px] break-all" data-testid="signature-public-url">
                {result.public_url}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <RecipientPicker value={sendTo} onChange={setSendTo} clientEmail={clientEmail} testIdPrefix="signature-send-to" />
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">
                Documento PDF <span className="text-red-600">*</span>
              </label>
              <div className="flex items-center gap-2">
                <label
                  htmlFor="signature-pdf-input"
                  className="flex items-center gap-2 px-3 py-2 border rounded cursor-pointer hover:bg-slate-50 text-sm"
                  data-testid="signature-file-label"
                >
                  <UploadSimple size={16} />
                  {file ? "Cambia file" : "Scegli file PDF"}
                </label>
                <input
                  id="signature-pdf-input"
                  type="file"
                  accept="application/pdf"
                  onChange={onFileChange}
                  className="hidden"
                  data-testid="signature-file-input"
                />
                {file && (
                  <div className="flex items-center gap-1 text-xs text-slate-600" data-testid="signature-file-preview">
                    <FilePdf size={16} weight="fill" className="text-red-600" />
                    <span className="truncate max-w-[220px]">{file.name}</span>
                    <span className="text-muted-foreground">
                      ({(file.size / 1024).toFixed(0)} KB)
                    </span>
                  </div>
                )}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">Solo PDF, max 20 MB.</div>
            </div>

            {/* Iter208.94 — Anteprima PDF + click-to-place */}
            {file && fileUrl && (
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Anteprima e punti firma ({positions.length} posizionat{positions.length === 1 ? "o" : "i"})
                </label>
                <SignaturePositionEditor
                  file={fileUrl}
                  positions={positions}
                  onChange={setPositions}
                />
              </div>
            )}

            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">
                Messaggio (opzionale)
              </label>
              <Textarea
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 5000))}
                maxLength={5000}
                placeholder="Es. Buongiorno, in allegato trova il contratto preliminare da firmare e restituire tramite il link nell'email. Grazie."
                className="text-sm"
                data-testid="signature-message-input"
              />
              <div className="text-[10px] text-slate-500 mt-1">
                {message.length}/5000 caratteri
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={closeDialog}
            disabled={sending}
            data-testid="signature-cancel"
          >
            {result?.ok ? "Chiudi" : "Annulla"}
          </Button>
          {!result?.ok && (
            <>
              <Button
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                disabled={sending || !file || positions.length === 0}
                data-testid="signature-preview-open"
                title="Anteprima del PDF come lo vedrà il firmatario"
              >
                <Eye size={14} className="mr-2" weight="bold" /> Anteprima firmata
              </Button>
              <Button
                className="ht-gradient border-0 text-white"
                onClick={submit}
                disabled={sending || !file || positions.length === 0 || !sendTo}
                data-testid="signature-submit"
              >
                {sending ? (
                  <>Invio…</>
                ) : (
                  <><PaperPlaneTilt size={14} className="mr-2" weight="bold" /> Invia da firmare</>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Iter208.95 — Anteprima Firmata read-only */}
      <Dialog open={previewOpen} onOpenChange={(v) => !v && setPreviewOpen(false)}>
        <DialogContent
          className="max-w-3xl max-h-[95vh] overflow-y-auto"
          data-testid="signature-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={18} weight="bold" /> Anteprima firmata
            </DialogTitle>
            <DialogDescription>
              Ecco come il firmatario vedrà il documento: i riquadri viola
              indicano i punti in cui dovrà apporre la firma. Chiudi per tornare
              indietro e modificare le posizioni se necessario.
            </DialogDescription>
          </DialogHeader>
          {file && fileUrl && (
            <div className="py-2" data-testid="signature-preview-body">
              <SignaturePositionEditor
                file={fileUrl}
                positions={positions}
                onChange={() => {}}
                readOnly
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
              data-testid="signature-preview-close"
            >
              Chiudi anteprima
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default SendForSignatureDialog;
